import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Card, TextField, Button, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import GanttTimeline from "components/GanttTimeline";
import axios from "axios";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';

const ScheduleOverview = () => {
  const [groupedSchedules, setGroupedSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filter states
  const [carIdFilter, setCarIdFilter] = useState('');
  const [driverIdFilter, setDriverIdFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false); // State to toggle filter visibility

  // Data for dropdowns
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    driver_id: '',
    car_id: '',
    start_time: null,
    end_time: null,
    date: null
  });
  const [conflictingSchedules, setConflictingSchedules] = useState([]);
  const [timeValidationError, setTimeValidationError] = useState('');

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Build filter parameters
      const params = {};
      if (carIdFilter !== null) params.car_id = carIdFilter;
      if (driverIdFilter !== null) params.driver_id = driverIdFilter;
      if (startDateFilter) params.start_date = startDateFilter;
      if (endDateFilter) params.end_date = endDateFilter;

      const response = await axios.get("https://api.csc-taxi.tech/api/admin/schedules/", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'ar'
        },
        params: params 
      });
      if (response.data.status === 1) {
        const groupedSchedules = {};

        // Iterate through the daily schedules and group by car
        Object.entries(response.data.data).forEach(([date, daySchedules]) => {
          daySchedules.forEach(schedule => {
            const carId = schedule.car?.id || `unknown-car-${Math.random()}`; 

            if (!groupedSchedules[carId]) {
              groupedSchedules[carId] = {
                car: schedule.car, 
                schedules: []
              };
            }
            groupedSchedules[carId].schedules.push({
              id: schedule.id,
              date,
              start_time: schedule.start_time,
              end_time: schedule.end_time,
              driver: schedule.driver 
            });
          });
        });

        setGroupedSchedules(groupedSchedules);
      } else {
        setError("Failed to fetch schedules");
      }
    } catch (err) {
      if (err.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/authentication/sign-in";
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [carIdFilter, driverIdFilter, startDateFilter, endDateFilter, conflictingSchedules]);

  // Fetch cars and drivers for dropdowns
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Fetch cars
        const carsResponse = await axios.get("https://api.csc-taxi.tech/api/admin/cars", {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          }
        });
        if (carsResponse.data.status === 1) {
          setCars(carsResponse.data.data);
        }

        // Fetch drivers
        const driversResponse = await axios.get("https://api.csc-taxi.tech/api/admin/driver", {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          }
        });
        if (driversResponse.data.status === 1) {
          setDrivers(driversResponse.data.data);
        }

      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchFilterData();
  }, []); // Fetch data only once on component mount

  const handleClearFilters = () => {
    setCarIdFilter('');
    setDriverIdFilter('');
    setStartDateFilter(null);
    setEndDateFilter(null);
  };

  const handleAddSchedule = async () => {
    setTimeValidationError(''); // Clear previous validation errors

    // Basic validation: Check if times are selected
    if (!newSchedule.start_time || !newSchedule.end_time) {
      setTimeValidationError('Please select both start and end times.');
      return;
    }

    // Time validation using Date objects
    if (newSchedule.end_time <= newSchedule.start_time) {
       setTimeValidationError('End time must be after start time.');
       return;
    }

    try {
      const token = localStorage.getItem("token");
      
      // Format the date to YYYY-MM-DD using local date components
      let formattedDate = null;
      if (newSchedule.date) {
        const date = newSchedule.date;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = date.getDate().toString().padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }

      // Format times to HH:mm:ss for the API
      const formattedStartTime = newSchedule.start_time ? 
        `${newSchedule.start_time.getHours().toString().padStart(2, '0')}:${newSchedule.start_time.getMinutes().toString().padStart(2, '0')}:00` : null;
      const formattedEndTime = newSchedule.end_time ? 
        `${newSchedule.end_time.getHours().toString().padStart(2, '0')}:${newSchedule.end_time.getMinutes().toString().padStart(2, '0')}:00` : null;

      const scheduleData = {
        driver_id: newSchedule.driver_id,
        car_id: newSchedule.car_id,
        date: formattedDate,
        start_time: formattedStartTime,
        end_time: formattedEndTime,
      };

      const response = await axios.post("https://api.csc-taxi.tech/api/admin/schedules", scheduleData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 200 || response.data.status === 1) {
        fetchSchedules();
        setOpenAddModal(false);
        setNewSchedule({
          driver_id: '',
          car_id: '',
          start_time: null,
          end_time: null,
          date: null
        });
        setConflictingSchedules([]);
      }
    } catch (error) {
      console.error('Error adding schedule:', error);
      // Check if the error is a 500 conflict with status 0
      if (error.response && error.response.status === 500 && error.response.data && error.response.data.status === 0) {
        console.log('Conflict detected:', error.response.data.data);
        setOpenAddModal(false);
        setConflictingSchedules(error.response.data.data); // Store conflicting schedules
        // Do NOT refresh the page
      } else {
        // Handle other errors (e.g., show a generic error message)
        setError(error.message);
      }
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`https://api.csc-taxi.tech/api/admin/schedules/${scheduleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 200 || response.data.status === 1) {
        fetchSchedules(); // Refresh the schedules after deletion
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setError(error.message);
    }
  };

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </VuiBox>
    );
  }

  if (error) {
    return (
      <VuiBox p={2}>
        <VuiTypography variant="h6" color="error">
          Error: {error}
        </VuiTypography>
      </VuiBox>
    );
  }

  const formControlSx = {
    marginTop: 0,
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      borderRadius: '8px',
      color: '#333',
      '& .MuiSelect-select': {
        color: '#333 !important',
        WebkitTextFillColor: '#333 !important',
      },
      '& fieldset': {
        borderColor: '#BDBDBD',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
      fontWeight: 400,
      fontSize: 14,
      top: '-3px',
      background: 'transparent !important',
      px: 0.5,
      '&.Mui-focused': {
        color: '#fff',
        background: 'transparent !important',
        px: 0,
      },
      '&.MuiInputLabel-shrink': {
        top: '-6px',
        background: 'transparent !important',
      }
    },
    '& .MuiSvgIcon-root': {
      color: '#333',
    },
  };

  const textFieldSx = {
    marginTop: 0,
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      borderRadius: '8px',
      color: '#333',
      '& fieldset': {
        borderColor: '#BDBDBD',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
      fontWeight: 400,
      fontSize: 14,
      top: '-3px',
      background: 'transparent !important',
      px: 0.5,
      '&.Mui-focused': {
        color: '#fff',
        background: 'transparent !important',
        px: 0,
      },
      '&.MuiInputLabel-shrink': {
        top: '-6px',
        background: 'transparent !important',
      }
    },
    '& .MuiSvgIcon-root': {
      color: '#333',
    },
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <VuiTypography variant="lg" fontWeight="bold" color="white">
            Driver & Car Schedules
          </VuiTypography>
          <VuiBox>
            <Button
              variant="contained"
              color="success"
              onClick={() => setOpenAddModal(true)}
              sx={{
                backgroundColor: '#43a047 !important',
                color: 'white !important',
                mr: 2
              }}
            >
              Add New Schedule
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: '#1E2A49 !important',
                color: 'white !important',
              }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </VuiBox>
        </VuiBox>
        
        <Collapse in={showFilters}>
          <VuiBox mb={2} p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth margin="normal" sx={formControlSx} size="small">
                    <InputLabel id="car-select-label" shrink>Car</InputLabel>
                    <Select
                      labelId="car-select-label"
                      value={carIdFilter}
                      label="Car"
                      onChange={(e) => setCarIdFilter(e.target.value)}
                      sx={{
                        color: '#333 !important',
                        WebkitTextFillColor: '#333 !important',
                        '& .MuiMenuItem-root': {
                          color: '#333',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                        },
                      }}
                    >
                      <MenuItem value="">All Cars</MenuItem>
                      {cars.map((car) => (
                        <MenuItem key={car.id} value={car.id}>
                          {car.manufacturer} {car.model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth margin="normal" sx={formControlSx} size="small">
                    <InputLabel id="driver-select-label" shrink>Driver</InputLabel>
                    <Select
                      labelId="driver-select-label"
                      value={driverIdFilter}
                      label="Driver"
                      onChange={(e) => setDriverIdFilter(e.target.value)}
                      sx={{
                        color: '#333 !important',
                        WebkitTextFillColor: '#333 !important',
                        '& .MuiMenuItem-root': {
                          color: '#333',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                        },
                      }}
                    >
                      <MenuItem value="">All Drivers</MenuItem>
                      {drivers.map((driver) => (
                        <MenuItem key={driver.id} value={driver.id}>
                          {driver.first_name} {driver.last_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDateFilter}
                    onChange={(newValue) => setStartDateFilter(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        sx={textFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDateFilter}
                    onChange={(newValue) => setEndDateFilter(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        sx={textFieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
            <VuiBox display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="text"
                onClick={handleClearFilters}
                sx={{ color: 'white !important' }}
              >
                Clear Filters
              </Button>
            </VuiBox>
          </VuiBox>
        </Collapse>

        <VuiBox
          sx={{
            backgroundColor: "rgba(255,255,255,0.05)",
            borderRadius: "8px",
            p: 2,
          }}
        >
          <GanttTimeline 
            groupedSchedules={groupedSchedules} 
            conflictingSchedules={conflictingSchedules} 
            onDeleteSchedule={handleDeleteSchedule}
            key={conflictingSchedules.length > 0 ? conflictingSchedules.map(s => s.id).join('-') : 'no-conflict'}
          />
        </VuiBox>

        {/* Add Schedule Modal */}
        <Dialog 
          open={openAddModal} 
          onClose={() => {
            setOpenAddModal(false);
            setTimeValidationError(''); // Clear error on close
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(126.97deg, rgba(6, 11, 40, 0.94) 28.26%, rgba(10, 14, 35, 0.49) 91.2%)',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
            }
          }}
        >
          <DialogTitle sx={{ color: 'white' }}>Add New Schedule</DialogTitle>
          <DialogContent>
            <VuiBox mt={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={formControlSx}>
                      <InputLabel id="new-schedule-car-label" shrink>Car</InputLabel>
                      <Select
                        labelId="new-schedule-car-label"
                        value={newSchedule.car_id}
                        label="Car"
                        onChange={(e) => setNewSchedule({...newSchedule, car_id: e.target.value})}
                        sx={{
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                          '& .MuiMenuItem-root': {
                            color: '#333',
                          },
                        }}
                      >
                        {cars.map((car) => (
                          <MenuItem key={car.id} value={car.id}>
                            {car.manufacturer} {car.model}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={formControlSx}>
                      <InputLabel id="new-schedule-driver-label" shrink>Driver</InputLabel>
                      <Select
                        labelId="new-schedule-driver-label"
                        value={newSchedule.driver_id}
                        label="Driver"
                        onChange={(e) => setNewSchedule({...newSchedule, driver_id: e.target.value})}
                        sx={{
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                          '& .MuiMenuItem-root': {
                            color: '#333',
                          },
                        }}
                      >
                        {drivers.map((driver) => (
                          <MenuItem key={driver.id} value={driver.id}>
                            {driver.first_name} {driver.last_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Start Time"
                      value={newSchedule.start_time}
                      onChange={(newValue) => setNewSchedule({...newSchedule, start_time: newValue})}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          sx={textFieldSx}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="End Time"
                      value={newSchedule.end_time}
                      onChange={(newValue) => setNewSchedule({...newSchedule, end_time: newValue})}
                      minTime={newSchedule.start_time}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          sx={textFieldSx}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Date"
                      value={newSchedule.date}
                      onChange={(newValue) => setNewSchedule({...newSchedule, date: newValue})}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          sx={textFieldSx}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
              {timeValidationError && (
                <VuiTypography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {timeValidationError}
                </VuiTypography>
              )}
            </VuiBox>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => {
                setOpenAddModal(false);
                setTimeValidationError(''); // Clear error on close
              }}
              sx={{ color: 'white' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddSchedule}
              variant="contained"
              color="success"
              sx={{
                backgroundColor: '#43a047 !important',
                color: 'white !important',
              }}
            >
              Add Schedule
            </Button>
          </DialogActions>
        </Dialog>
      </VuiBox>
    </Card>
  );
};

export default ScheduleOverview;